import $ from "jquery";
import ko from "knockout";
import workspace from "flow/viewModels/workspace";
import dash from "flow/viewModels/dash";
import heartbeat from "flow/heartbeat";
import * as splash from "flow/splash/splash";
import * as toastr from "toastr";
import manifestManager from "flow/utils/manifestManager";
import config from "flow/config";
import router from "flow/router";
import session from "flow/session/session";
import metaCache from "flow/utils/metaCache";
import sysLogger from "flow/sysLogger";
import baseUrl from "flow/wrappers/baseUrl";
import environment from "flow/configuration/environment";
import * as workspaceLoader from "flow/init/workspaceLoader";
import { setupAntiCSRFJQuery } from "flow/security/csrf";
import { setupValidationDefaults } from "flow/knockout/validationSetup";
import registerTimerWorkerFactory from "flow/workers/registerTimerWorkerFactory";
import { loadAppConfigIntoEnvironment } from "./configuration/appConfig";

export function run(window: Window) {
    setupAjaxBehavior();
    setupAntiCSRFJQuery();

    const myFlow = workspace.createMyFlow();

    environment.setup(myFlow);

    // Find out our culture for this page lifetime
    loadAppConfigIntoEnvironment();

    registerGlobalErrorHandler(window);

    configureToastr();

    sysLogger.setSysLogUrl(baseUrl);

    registerTimerWorkerFactory();

    workspace.earlySetup();

    // Needs to run before first knockout applyBindings.
    setupValidationDefaults();

    splash.mount();

    workspaceLoader.readWorkspace(myFlow).done((continueBootup: boolean) => {
        if (!continueBootup) {
            // Product selector determined that we should not continue bootup (e.g. because we jumped to Flow 3), so we exit early here.
            return;
        }

        workspace.init();

        ko.applyBindings(workspace, document.body);

        dash.init();

        ko.applyBindings(dash, dash.root);

        document.addEventListener("keydown", function (event) {
            if (!event.defaultPrevented && event.keyCode && event.keyCode === $.ui.keyCode.ESCAPE) {
                dash.show();
            }
        });

        const heartbeatInterval = environment.get<number>(environment.keys.heartbeatInterval);

        const webModuleUrls = manifestManager.getWebModuleUrls();
        webModuleUrls.forEach(url => {
            heartbeat.keepAlive(url, heartbeatInterval);
        });

        registerExitHandler(window);

        preventImplicitFormSubmission();

        router.init(workspace);

        splash.unmount();

        $(".bindingNotApplied").removeClass("bindingNotApplied");
    });
}

function registerGlobalErrorHandler(window: Window) {
    window.onerror = workspace.reportUncaughtError;
    window.onunhandledrejection = workspace.reportUnhandledRejection;
}

function configureToastr() {
    toastr.options.positionClass = "toast-bottom-right";
    toastr.options.newestOnTop = false;
    toastr.options.iconClasses = {
        error: "lc-toastr-error",
        info: "lc-toastr-info",
        success: "lc-toastr-success",
        warning: "lc-toastr-warning"
    };
}

function registerExitHandler(window: Window) {
    window.onbeforeunload = function (e) {
        if (session.forcedLogout) {
            delete e["returnValue"];
            return undefined;
        }

        if (!environment.ignorePageUnloadDirtyCheck()) {
            const canLeaveResult = workspace.collectCanLeaveResults();

            if (canLeaveResult.length > 0) {
                // Custom text is ignored by most, if not all, browsers now, but just in case
                const message = metaCache.lookup<string>(
                    "FlowHtml.Translations.YouHaveUnsavedData",
                    "You still have unsaved data."
                );
                return message;
            }
        }

        delete e["returnValue"];
        return undefined;
    };
}

function preventImplicitFormSubmission() {
    if (config.preventImplicitFormSubmission) {
        $(document).on("submit", "form", function (event) {
            event.preventDefault();
        });
    }
}

function setupAjaxBehavior() {
    $(document).ajaxSend(function (_e, xhr) {
        xhr.setRequestHeader("ajax-no-cross-domain-redirect", "true");
    });
}
